/**
 * Ticket Lifecycle v2 — Slice 5D-3
 *
 * Pure helper that groups advisor-attributed ticket slices by job-mix
 * bucket and produces averages + confidence per bucket.
 *
 * Companion to `computeAdvisorMetrics` (slice 4E + 5A.3). Where the
 * sibling helper returns shop-wide / per-advisor scalars over the
 * window, this helper partitions by `TicketJobMixBucket` so future
 * Lifecycle OptiTime prediction (slice 5D-4) can pick a per-advisor
 * × per-bucket historical average.
 *
 * Scope: **analytics math only.** This helper:
 *   - does NOT touch `tickets.opti_time` / `tickets.opti_finish`
 *   - does NOT touch Chronomancer / scheduling / smart assign / timer
 *     / parking / rollover / bay assignment
 *   - does NOT consume Tech OptiTime (technician/bay) data — the
 *     advisor pipeline stays separate per spec §3.3
 *   - does NOT feed Lifecycle OptiTime prediction yet (slice 5D-4 will)
 *
 * Attribution contract (caller responsibility):
 *   Each `AdvisorBucketTicketSlice` MUST already represent one advisor's
 *   custody cycle for a single ticket. The caller (typically
 *   `useAdvisorBucketMetrics`) is responsible for:
 *     - matching `foh_accept` events to the target advisor's `actor_id`
 *     - computing per-ticket interval durations from `ticket_custody_events`
 *     - reading `remaining_approved_work_minutes` from the
 *       `ticket_accepted` advisor_action_events.metadata payload
 *       captured in slice 5D-2
 *   This helper ONLY groups + averages; it does not re-derive any of
 *   the inputs.
 *
 * Deferred delay-impact branches (treated as null per slice 5D-3 spec):
 *   - `diag_plus_work` with missing `remainingApprovedWorkMinutes` →
 *     `calculateAdvisorDelayImpact` returns `0` with method
 *     `'overlap_adjusted'` and a "deferred" note. The bucket-level
 *     average should NOT pull that 0 into the mean (it would silently
 *     under-state typical impact). Treated as null and excluded from
 *     the per-ticket sample for `averageAdvisorDelayImpactMinutes`.
 *   - `work_with_dependency` always returns method
 *     `'dependency_deferred'` until a reliable hold/dependency signal
 *     lands. Same null treatment.
 *
 * Boundary: imports only the three pure helpers in this module
 * (`jobMixClassifier`, `advisorDelayImpact`, `remainingApprovedWork`).
 * It must not be imported into Chronomancer / scheduling / timer /
 * parking / rollover / bay / invariants hot paths — the drift detector
 * boundary guard pins this.
 */
import {
  calculateAdvisorDelayImpact,
  type AdvisorDelayImpactMethod,
} from './advisorDelayImpact';
import type { TicketJobMixBucket } from './jobMixClassifier';

/**
 * Closed set of buckets emitted in the result map, in fixed display
 * order. Mirrors `jobMixClassifier`'s `TicketJobMixBucket` union and
 * is pinned by the drift detector for sync.
 */
export const ADVISOR_BUCKET_ORDER: ReadonlyArray<TicketJobMixBucket> = [
  'diag_only',
  'diag_plus_work',
  'work_only',
  'work_with_dependency',
  'comeback_recheck',
];

/**
 * Confidence thresholds (per slice 5D-3 spec):
 *   high: n ≥ 30; medium: n ≥ 10; low: n ≥ 3; insufficient: n < 3.
 *
 * Match the same sample-size doctrine as
 * `kpi_technician_efficiency` (`null` ratio below 3) — except this
 * helper reports a categorical confidence rather than NULL so a UI
 * can still surface "low confidence" alongside the mean.
 */
export type AdvisorBucketConfidence =
  | 'high'
  | 'medium'
  | 'low'
  | 'insufficient';

export const ADVISOR_BUCKET_CONFIDENCE_THRESHOLDS = {
  high: 30,
  medium: 10,
  low: 3,
} as const;

export function classifyAdvisorBucketConfidence(
  sampleSize: number,
): AdvisorBucketConfidence {
  if (sampleSize >= ADVISOR_BUCKET_CONFIDENCE_THRESHOLDS.high) return 'high';
  if (sampleSize >= ADVISOR_BUCKET_CONFIDENCE_THRESHOLDS.medium) return 'medium';
  if (sampleSize >= ADVISOR_BUCKET_CONFIDENCE_THRESHOLDS.low) return 'low';
  return 'insufficient';
}

/**
 * One advisor's cycle on one ticket. All values pre-computed by the
 * caller. `null` means "not available for this ticket" — averages will
 * skip nulls per metric rather than coercing to 0.
 */
export interface AdvisorBucketTicketSlice {
  ticketId: string;
  bucket: TicketJobMixBucket;
  /** From `deriveTimeBuckets(...).advisorObservedMinutes`. */
  advisorObservedMinutes: number | null;
  /** kick_to_foh → foh_accept (this advisor's accept). */
  acceptLagMinutes: number | null;
  /** foh_accept → kick_to_boh (this advisor's accept). */
  fohCustodyMinutes: number | null;
  /** Full handoff cycle: kick_to_foh → boh_accept, attributed to this advisor. */
  advisorLifecycleMinutes: number | null;
  /**
   * Snapshot from advisor_action_events.metadata.remaining_approved_work_minutes,
   * captured at FOH accept in slice 5D-2. `null` when the snapshot was
   * missing or the helper refused at capture time.
   */
  remainingApprovedWorkMinutes: number | null;
}

export interface AdvisorBucketMetricsInput {
  advisorUserId: string;
  /**
   * Per-ticket slices already attributed to `advisorUserId`. Tickets the
   * caller could not attribute MUST be filtered out upstream — this
   * helper does not re-check ownership.
   */
  tickets: ReadonlyArray<AdvisorBucketTicketSlice>;
}

export interface AdvisorBucketMetric {
  bucket: TicketJobMixBucket;
  ticketSampleSize: number;
  confidence: AdvisorBucketConfidence;
  averageAcceptLagMinutes: number | null;
  averageFohCustodyMinutes: number | null;
  averageAdvisorLifecycleMinutes: number | null;
  averageAdvisorObservedMinutes: number | null;
  /**
   * Per-ticket `calculateAdvisorDelayImpact` averaged across the
   * bucket's sample. Tickets with deferred delay-impact branches
   * (diag_plus_work missing remaining; work_with_dependency) are
   * excluded from this sample rather than counted as zero — see module
   * doc.
   */
  averageAdvisorDelayImpactMinutes: number | null;
  /** How many tickets contributed to `averageAdvisorDelayImpactMinutes`. */
  delayImpactSampleSize: number;
}

export type AdvisorBucketMetricsResult = Record<
  TicketJobMixBucket,
  AdvisorBucketMetric
>;

// ─── Pure helpers ───────────────────────────────────────────────────────

function meanOrNull(values: ReadonlyArray<number | null>): number | null {
  let sum = 0;
  let n = 0;
  for (const v of values) {
    if (v !== null && Number.isFinite(v)) {
      sum += v;
      n += 1;
    }
  }
  return n === 0 ? null : sum / n;
}

/**
 * Compute a single ticket's delay impact contribution using the
 * existing pure helper. Returns `null` for deferred branches per the
 * slice 5D-3 spec (so they do not pull the bucket average toward 0).
 *
 * Exported for unit testing — the logic is small enough to be inlined
 * but factoring lets the test pin the exact deferred-branch behavior.
 */
export function ticketDelayImpactContribution(
  slice: AdvisorBucketTicketSlice,
): { minutes: number | null; method: AdvisorDelayImpactMethod | null } {
  if (slice.advisorObservedMinutes === null) {
    return { minutes: null, method: null };
  }
  // `dependency_deferred` is the helper's documented "0 placeholder
  // until reliable signal" — exclude from the bucket mean.
  if (slice.bucket === 'work_with_dependency') {
    return { minutes: null, method: 'dependency_deferred' };
  }
  // `overlap_adjusted` returns 0 when remaining-approved-work is null;
  // exclude from the mean rather than under-state typical impact.
  if (
    slice.bucket === 'diag_plus_work' &&
    slice.remainingApprovedWorkMinutes === null
  ) {
    return { minutes: null, method: 'overlap_adjusted' };
  }
  const result = calculateAdvisorDelayImpact({
    advisorObservedMinutes: slice.advisorObservedMinutes,
    jobMixBucket: slice.bucket,
    remainingApprovedWorkMinutes: slice.remainingApprovedWorkMinutes,
  });
  return {
    minutes: result.advisorDelayImpactMinutes,
    method: result.method,
  };
}

function emptyBucketMetric(bucket: TicketJobMixBucket): AdvisorBucketMetric {
  return {
    bucket,
    ticketSampleSize: 0,
    confidence: classifyAdvisorBucketConfidence(0),
    averageAcceptLagMinutes: null,
    averageFohCustodyMinutes: null,
    averageAdvisorLifecycleMinutes: null,
    averageAdvisorObservedMinutes: null,
    averageAdvisorDelayImpactMinutes: null,
    delayImpactSampleSize: 0,
  };
}

/**
 * Group ticket slices by bucket and emit one metric per bucket.
 * Always returns all 5 buckets — empty buckets surface with
 * `sampleSize: 0` and `confidence: 'insufficient'` so consumers can
 * render a complete table without `undefined` checks.
 *
 * Pure: same input ⇒ same output; does not mutate `input.tickets`.
 */
export function computeAdvisorBucketMetrics(
  input: AdvisorBucketMetricsInput,
): AdvisorBucketMetricsResult {
  const result: AdvisorBucketMetricsResult = {
    diag_only: emptyBucketMetric('diag_only'),
    diag_plus_work: emptyBucketMetric('diag_plus_work'),
    work_only: emptyBucketMetric('work_only'),
    work_with_dependency: emptyBucketMetric('work_with_dependency'),
    comeback_recheck: emptyBucketMetric('comeback_recheck'),
  };

  // Group slices by bucket. Use a fresh per-bucket scratch so the
  // result rebuild is straightforward and we never mutate the input.
  const grouped: Record<TicketJobMixBucket, AdvisorBucketTicketSlice[]> = {
    diag_only: [],
    diag_plus_work: [],
    work_only: [],
    work_with_dependency: [],
    comeback_recheck: [],
  };
  for (const slice of input.tickets) {
    // Defensive: a slice with a bucket value outside the union is
    // ignored. The TypeScript union prevents this in typed callers;
    // the runtime guard protects callers that cast through `any`.
    if (grouped[slice.bucket]) {
      grouped[slice.bucket].push(slice);
    }
  }

  for (const bucket of ADVISOR_BUCKET_ORDER) {
    const slices = grouped[bucket];
    const sampleSize = slices.length;
    if (sampleSize === 0) continue;

    const acceptLags: Array<number | null> = [];
    const fohCustody: Array<number | null> = [];
    const lifecycles: Array<number | null> = [];
    const observed: Array<number | null> = [];
    const delayImpacts: number[] = [];

    for (const slice of slices) {
      acceptLags.push(slice.acceptLagMinutes);
      fohCustody.push(slice.fohCustodyMinutes);
      lifecycles.push(slice.advisorLifecycleMinutes);
      observed.push(slice.advisorObservedMinutes);
      const impact = ticketDelayImpactContribution(slice);
      if (impact.minutes !== null && Number.isFinite(impact.minutes)) {
        delayImpacts.push(impact.minutes);
      }
    }

    result[bucket] = {
      bucket,
      ticketSampleSize: sampleSize,
      confidence: classifyAdvisorBucketConfidence(sampleSize),
      averageAcceptLagMinutes: meanOrNull(acceptLags),
      averageFohCustodyMinutes: meanOrNull(fohCustody),
      averageAdvisorLifecycleMinutes: meanOrNull(lifecycles),
      averageAdvisorObservedMinutes: meanOrNull(observed),
      averageAdvisorDelayImpactMinutes:
        delayImpacts.length === 0
          ? null
          : delayImpacts.reduce((a, b) => a + b, 0) / delayImpacts.length,
      delayImpactSampleSize: delayImpacts.length,
    };
  }

  return result;
}
